import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { BeakerIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import z from 'zod';

import { Alert, AlertDescription, AlertTitle } from '@/components/core/alert';
import { Button } from '@/components/core/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { TextFormField } from '@/components/forms/text-form-field';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page, PageContent } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import type { CreateSimulationScenarioDTO } from '@/gateways/impl/SimulationGateway';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { useMessages } from '@/shared/providers/MessagesProvider';

import { canCreateScenarioForInputsStatus, disabledScenarioReason } from '../shared/inputs-status';
import { RunConfigFields } from '../shared/run-config-fields';

const schema = z.object({
  name: z.string().optional(),
  accountId: z.string().min(1, 'Select an account'),
  locationId: z.string().min(1, 'Select a location'),
  carrierServiceIds: z.array(z.string()).min(1, 'Pick at least one carrier service'),
});

type FormValues = z.infer<typeof schema>;

export function CreateScenarioPage() {
  const { id: simulationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { simulationGateway } = useGateways();
  const messages = useMessages();

  const { data: simulation, isLoading: isLoadingSimulation } = useQuery({
    queryKey: ['simulations', simulationId],
    queryFn: ({ signal }) => simulationGateway.getSimulation(simulationId!, { signal }),
    enabled: !!simulationId,
    select: (data) => data.simulation,
  });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      accountId: '',
      locationId: '',
      carrierServiceIds: [],
    },
  });

  const { mutate: createScenarioMutate } = useGatewayMutation({
    mutationFn: (input: CreateSimulationScenarioDTO) =>
      simulationGateway.createSimulationScenario(simulationId!, input),
    linkedQuery: ['simulations', simulationId],
    linkResponseToQuery: false,
  });

  const canCreateScenario = canCreateScenarioForInputsStatus(simulation?.inputsStatus);
  const scenarioDisabledReason =
    !isLoadingSimulation && !canCreateScenario
      ? disabledScenarioReason(simulation?.inputsStatus)
      : undefined;

  const onSubmit = async (data: FormValues) => {
    if (!canCreateScenario) {
      messages.error(disabledScenarioReason(simulation?.inputsStatus));
      return;
    }

    const result = await createScenarioMutate({
      name: data.name?.trim() ? data.name.trim() : null,
      accountId: data.accountId,
      locationId: data.locationId,
      carrierServiceIds: data.carrierServiceIds,
    });
    if (result.isFailure) return;
    messages.success('Scenario started');
    void navigate(`/simulations/${simulationId}`);
  };

  const { isSubmitting } = form.formState;
  const placeholder = simulation?.name ? `${simulation.name} — Baseline` : 'e.g. Baseline margin';

  return (
    <Page>
      <Header>
        <HeaderIcon
          parentUrl={`/simulations/${simulationId}`}
          parentTitle={simulation?.name ?? 'Simulation'}
        >
          <BeakerIcon />
        </HeaderIcon>
        <HeaderTitle>New scenario</HeaderTitle>
        <HeaderActions>
          <Button
            type="submit"
            form="create-scenario-form"
            size="sm"
            loading={isSubmitting}
            disabled={isSubmitting || isLoadingSimulation || !!scenarioDisabledReason}
          >
            Start
          </Button>
        </HeaderActions>
      </Header>
      <PageContent variant="column">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
          <Form {...form}>
            <form
              id="create-scenario-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="contents"
            >
              {scenarioDisabledReason ? (
                <Alert variant="error">
                  <AlertTitle>Scenario unavailable</AlertTitle>
                  <AlertDescription>{scenarioDisabledReason}</AlertDescription>
                </Alert>
              ) : null}
              <Card>
                <CardHeader>
                  <CardTitle>Scenario</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">
                  <TextFormField
                    control={form.control}
                    name="name"
                    label="Scenario name (optional)"
                    placeholder={placeholder}
                    description="Prefix with the simulation name to make comparisons easy to scan."
                  />
                  <RunConfigFields
                    control={form.control}
                    accountFieldName="accountId"
                    locationFieldName="locationId"
                    carrierServicesFieldName="carrierServiceIds"
                  />
                </CardContent>
              </Card>
            </form>
          </Form>
        </div>
      </PageContent>
    </Page>
  );
}
